import { Grid } from "@material-ui/core";
import { FC, useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { mixed, object, string } from "yup";
import { FileField } from "../../../../components/forms/file-3";
import { RadioGroup } from "../../../../components/forms/radio";
import { GridItem } from "../../../../components/layout";
import { colours } from "../../../../styles/colours";
import {
  AddressAttachmentType,
  useAddressAttachmentType,
} from "../../../common/models";
import { Field } from "../internal/field";
import { ColumnWrapper, FieldHook, RenderFuncProps, Wrapper } from "./common";

export const ErrorText = styled.div`
  color: ${colours.red};
`;

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const { control } = useFormContext();

  const typeField = useController({
    control,
    name: `${item.field}.type`,
  });
  const fileField = useController({
    control,
    name: `${item.field}.file`,
  });

  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);
  const addressAttachmentType = useAddressAttachmentType();
  const options = useMemo(
    () =>
      Field.isAddressAttachmentField(item) &&
      item.typeDetails.availableOptions?.[currentLanguage]
        ? (item.typeDetails.availableOptions[
            currentLanguage
          ] as AddressAttachmentType[])
        : [],
    [item, currentLanguage]
  );

  return (
    <Wrapper coloured>
      <Grid container spacing={3}>
        <GridItem top xs={12}>
          <strong>{t("proof-of-address")}</strong>
        </GridItem>
        <GridItem top xs={12}>
          {t("choose-your-document-type")}
        </GridItem>
        <GridItem top xs={12}>
          <RadioGroup<AddressAttachmentType>
            fullWidth
            inline
            options={options.map((v) => ({
              label:
                addressAttachmentType.enum[v as AddressAttachmentType] || v,
              id: v,
            }))}
            onChange={typeField.field.onChange}
            value={typeField.field.value}
          />
        </GridItem>
        <GridItem top xs={12}>
          {t("upload-your-document")}
        </GridItem>
        <GridItem top xs={12}>
          <ColumnWrapper>
            <FileField
              onChange={fileField.field.onChange}
              value={fileField.field.value}
              error={!!fileField.fieldState.error}
              helperText={fileField.fieldState.error?.message}
              label={t("address-attachment")}
            />
          </ColumnWrapper>
        </GridItem>
        <GridItem top xs={12}>
          <small>
            <em
              dangerouslySetInnerHTML={{
                __html: t("please-upload-the-document-clearly"),
              }}
            ></em>
          </small>
        </GridItem>
      </Grid>
    </Wrapper>
  );
};

const useSchema = () => {
  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);

  return useCallback(
    (f: Field.DefaultField) =>
      object()
        .shape({
          type: Field.isAddressAttachmentField(f)
            ? mixed()
                .oneOf(
                  (f.typeDetails.availableOptions?.[
                    currentLanguage
                  ] as AddressAttachmentType[]) || []
                )
                .label(t("address-attachment-type"))
                .required(
                  t("required-field", {
                    ns: "errors",
                    field: t("address-attachment-type"),
                  })
                )
            : string(),
          file: mixed()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("address-attachment-file"),
              })
            )
            .test(
              "is-large",
              t(`file-size-cannot-exceed-6-mb`),
              (file: any) =>
                !(file instanceof File) || file.size <= 6 * 1024 * 1024
            )
            .label(t("address-attachment-file")),
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("address-attachment"),
          })
        )
        .label(t("address-attachment")),

    [t, currentLanguage]
  );
};

export const useAddressAttachmentField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
      service: "ADDRESS",
    }),
    [schema]
  );
};
